import React from "react";
import { Table, Tag, Divider } from "antd";
import dayjs from "dayjs";
import { RotateCcw } from "lucide-react";
import { ReturnType } from "../../../Enums";

export const OrderReturnsSection = ({ order }: any) => {
  const hasReturns = (order?.returns?.length ?? 0) > 0;

  if (!hasReturns) return null;

  const column = [
    {
      title: "Product",
      dataIndex: "product_name",
      key: "product_name",
      render: (v: string, rec: any) => (
        <div>
          <div className="font-medium text-gray-800 text-xs">{v}</div>
          <div className="text-[10px] text-gray-400">
            SKU: {rec.sku_code || "N/A"}
          </div>
        </div>
      ),
    },
    {
      title: "Return Type",
      dataIndex: "return_type_name",
      key: "return_type_name",
      render: (v: string, rec: any) => {
        const isGood =
          rec.return_type_code === ReturnType.GOOD_RETURN ||
          rec.return_type_name?.toLowerCase().includes("good");
        const isDamaged =
          rec.return_type_code === ReturnType.DAMAGED_RETURN ||
          rec.return_type_name?.toLowerCase().includes("damaged");
        return (
          <Tag
            color={isGood ? "green" : isDamaged ? "orange" : "default"}
            className="text-[9px]"
          >
            {v || "N/A"}
          </Tag>
        );
      },
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      render: (v: number) => (
        <Tag color="orange" className="text-[10px]">
          {v}
        </Tag>
      ),
    },
    {
      title: "Refund Price",
      dataIndex: "refund_price",
      key: "refund_price",
      render: (v: number) =>
        `LKR ${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
    },
    {
      title: "Line Total",
      dataIndex: "total_refund",
      key: "total_refund",
      render: (v: number) => (
        <span className="font-bold text-orange-600 text-xs">
          LKR{" "}
          {v.toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      title: "Restocked",
      dataIndex: "stock_adjusted",
      key: "stock_adjusted",
      render: (v: number) => (
        <Tag color={v === 1 ? "green" : "default"} className="text-[9px]">
          {v === 1 ? "Yes" : "No"}
        </Tag>
      ),
    },
  ];
  return (
    <>
      <div>
        <h3 className="text-xs font-bold text-gray-800 mb-2 flex items-center gap-1.5">
          <RotateCcw className="w-3.5 h-3.5 text-orange-500" />
          Returns Logged ({order.returns!.length})
        </h3>
        {order.returns!.map((ret) => (
          <div
            key={ret.id}
            className="mb-3 border border-orange-100 bg-orange-50/30 p-3 rounded-lg"
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex flex-col gap-0.5">
                <span className="font-bold text-orange-600 text-xs">
                  {ret.return_no}
                </span>
                <span className="text-[10px] text-gray-400">
                  {dayjs(ret.return_date).format("YYYY-MM-DD HH:mm")}
                </span>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <span className="font-bold text-gray-800 text-xs">
                  Refunded: LKR{" "}
                  {ret.total_refunded.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </span>
                {ret.refund_payment_method_name && (
                  <Tag color="blue" className="text-[9px]">
                    {ret.refund_payment_method_name}
                  </Tag>
                )}
              </div>
            </div>
            {ret.note && (
              <p className="text-[10px] text-gray-500 italic mb-2">
                Note: {ret.note}
              </p>
            )}
            <Table
              dataSource={ret.items || []}
              size="small"
              pagination={false}
              rowKey="id"
              bordered
              columns={column}
            />
          </div>
        ))}
      </div>
    </>
  );
};
